/**
 * Portal language preference ownership (#172).
 *
 * Precedence, highest first:
 *   1. A language the signed-in customer chose in this session that has not
 *      been settled yet (latest intent always wins over any profile response).
 *   2. The authenticated profile language, when it arrives for the current
 *      account and no newer local choice was made since the load began.
 *   3. A valid language stored in this browser (`mbbe-portal-language`).
 *   4. The default (`tr`).
 * Portal copy itself falls back to Turkish for any key a locale lacks.
 *
 * The stored language is a per-browser preference, not a per-account one. It
 * deliberately carries across accounts: when the next account's profile has no
 * (valid) language, the visible language is left as it is and nothing is
 * written to that account's server profile.
 *
 * Save policy: one write in flight at a time, coalesced to the latest intent.
 * A request is never abandoned, so the backend always ends with the last
 * accepted choice. A failed request does NOT prove the server did not apply it
 * (a lost response is indistinguishable from a rejected write), so after any
 * failure the server value is "unknown" and the latest intent is always sent
 * again. Only a failure of the latest intent is reported; an older failure never
 * rolls back or toasts over a newer choice. On a final failure the server value
 * is read back (`readServerLanguage`) and adopted when still current; if the
 * read-back is unavailable the last known-good language is restored.
 */
export type PortalLanguage = 'tr' | 'en';

export const PORTAL_LANGUAGE_STORAGE_KEY = 'mbbe-portal-language';
export const DEFAULT_PORTAL_LANGUAGE: PortalLanguage = 'tr';

export function parsePortalLanguage(value: unknown): PortalLanguage | null {
  return value === 'tr' || value === 'en' ? value : null;
}

export function readStoredPortalLanguage(): PortalLanguage | null {
  try {
    return parsePortalLanguage(window.localStorage.getItem(PORTAL_LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function writeStoredPortalLanguage(language: PortalLanguage): boolean {
  try {
    window.localStorage.setItem(PORTAL_LANGUAGE_STORAGE_KEY, language);
    return true;
  } catch {
    return false;
  }
}

export interface LanguageSyncOptions {
  save: (language: PortalLanguage) => Promise<unknown>;
  getVisible: () => PortalLanguage;
  apply: (language: PortalLanguage) => void;
  onFinalFailure: (info: { failed: PortalLanguage; restored: PortalLanguage }) => void;
  /** Reads the language the server currently holds for the signed-in account. Optional; used to reconcile after a final failure. */
  readServerLanguage?: () => Promise<unknown>;
}

export interface LanguageSyncController {
  /** Binds the controller to the current account (null when signed out). Resets all pending state on change. */
  setAccount: (accountKey: string | null) => void;
  /** User-chosen language: applied immediately, persisted with the latest-intent policy. */
  select: (language: PortalLanguage) => void;
  /** Starts a profile load; the returned adopter applies the profile language only if it is still current. */
  beginProfileLoad: (accountKey: string) => (language: unknown) => boolean;
}

export function createLanguageSyncController(options: LanguageSyncOptions): LanguageSyncController {
  let account: string | null = null;
  let epoch = 0;
  let selections = 0;
  /** Language the server is known to hold; null when unknown (never confirmed, or after any failed write). */
  let confirmed: PortalLanguage | null = null;
  /** Last language known to have been on the server; only used to pick the value to restore. */
  let lastConfirmed: PortalLanguage | null = null;
  let baseline: PortalLanguage | null = null;
  let desired: PortalLanguage | null = null;
  let inFlight = false;

  const confirm = (language: PortalLanguage) => {
    confirmed = language;
    lastConfirmed = language;
  };

  const reconcileFinalFailure = (failed: PortalLanguage, fallback: PortalLanguage, requestEpoch: number) => {
    const startSelections = selections;
    const stillCurrent = () => requestEpoch === epoch && selections === startSelections && desired === null && !inFlight;
    const restoreAndReport = () => {
      if (!stillCurrent()) return;
      baseline = null;
      options.apply(fallback);
      options.onFinalFailure({ failed, restored: fallback });
    };
    if (!options.readServerLanguage) {
      restoreAndReport();
      return;
    }
    Promise.resolve()
      .then(() => options.readServerLanguage?.())
      .then(
        (raw) => {
          if (!stillCurrent()) return;
          const server = parsePortalLanguage(raw);
          if (!server) {
            restoreAndReport();
            return;
          }
          baseline = null;
          confirm(server);
          options.apply(server);
          // The write did reach the server after all: nothing to report.
          if (server !== failed) options.onFinalFailure({ failed, restored: server });
        },
        restoreAndReport,
      );
  };

  const settle = (requestEpoch: number, sent: PortalLanguage, ok: boolean) => {
    if (requestEpoch !== epoch) return;
    inFlight = false;
    if (ok) {
      confirm(sent);
      if (desired === sent) {
        desired = null;
        baseline = null;
        return;
      }
      pump();
      return;
    }
    // The outcome of a failed write is unknown: the server may have applied it.
    confirmed = null;
    if (desired !== sent) {
      pump();
      return;
    }
    const restored = lastConfirmed ?? baseline ?? options.getVisible();
    desired = null;
    // Held until the read-back settles so a choice made meanwhile restores to a known-good value.
    baseline = restored;
    reconcileFinalFailure(sent, restored, requestEpoch);
  };

  function pump() {
    if (inFlight || desired === null || account === null) return;
    if (confirmed !== null && desired === confirmed) {
      desired = null;
      baseline = null;
      return;
    }
    const language = desired;
    const requestEpoch = epoch;
    inFlight = true;
    Promise.resolve()
      .then(() => {
        // Never send under an account other than the one that made the choice.
        if (requestEpoch !== epoch) throw new Error('account changed before the language write');
        return options.save(language);
      })
      .then(
        () => settle(requestEpoch, language, true),
        () => settle(requestEpoch, language, false),
      );
  }

  return {
    setAccount(next) {
      if (next === account) return;
      account = next;
      epoch += 1;
      confirmed = null;
      lastConfirmed = null;
      baseline = null;
      desired = null;
      inFlight = false;
    },
    select(language) {
      selections += 1;
      if (account === null) {
        options.apply(language);
        return;
      }
      if (desired === null && !inFlight && lastConfirmed === null && baseline === null) baseline = options.getVisible();
      desired = language;
      options.apply(language);
      pump();
    },
    beginProfileLoad(accountKey) {
      const startEpoch = epoch;
      const startSelections = selections;
      return (raw) => {
        const language = parsePortalLanguage(raw);
        if (!language) return false;
        if (account !== accountKey || epoch !== startEpoch) return false;
        if (selections !== startSelections || desired !== null || inFlight) return false;
        confirm(language);
        options.apply(language);
        return true;
      };
    },
  };
}
